import React from 'react';
import { DateTime } from 'luxon';
import Player from '@vimeo/player';

import { PrimaryButton, SecondaryButton } from 'Components/Button';

import { resourceDateIsBeforeNow } from 'Services/DateHelpers';
import { translation } from 'Services/TranslationHelpers';
import {User} from "Services";

export default class Stream extends React.Component {
    vimeoRef = null;

    supposedCurrentTime = 0;
    canSeek = false;
    player = null;

    state = {
        muted: true,
        subtitles: false,
        useClevercast: false,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        if (this.props.stream.prerecorded) {
            this.prerecord();
        }
    };

    /**
     * @method componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    componentDidUpdate = (prevProps, prevState, snapshot) => {
        if (this.props.stream.prerecorded && this.player === null) {
            this.prerecord();
        }
    };

    /**
     * @method prerecord
     */
    prerecord = () => {
        this.player = new Player(this.vimeoRef);
        const { starts_at } = this.props.stream;

        const now = DateTime.now();
        const startedAt = DateTime.fromISO(starts_at.iso_string);

        if (resourceDateIsBeforeNow(starts_at)) {
            this.supposedCurrentTime = now.diff(startedAt).milliseconds / 1000;
            // const minutes = Math.floor(millis / 60000);
            // const seconds = ((millis % 60000) / 1000).toFixed(0);
            // startFrom = `${minutes}m${seconds}s`;

            this.player.setCurrentTime(this.supposedCurrentTime).then((seconds) => {
                // seconds = the actual time
            })
        }

        this.player.on('timeupdate', (data) => {
            this.player.getSeeking().then((seeking) => {
                if (!seeking) {
                    this.supposedCurrentTime = data.seconds;
                }
            });
        });

        this.player.on('seeking', (data) => {
            // NB: Uncomment to see the played ranges
            // logPlayedRange();
            if (this.canSeek) {
                return
            }
            // ?? NB: pause first if the position is near the end,
            // Because in fullscreen it can still continue to the end sometimes
            if (data.duration - data.seconds < 10) {
                this.player.pause()
            }
            // accept rewind
            // if (data.seconds < this.supposedCurrentTime) {
            //     return
            // }
            // accept seek to already played time
            isPlayed(data.seconds).then((played) => {
                if (played) {
                    return
                }

                // guard agains infinite recursion:
                // user seeks, seeking is fired, currentTime is modified, seeking is fired, current time is modified, ...
                var delta = data.seconds - this.supposedCurrentTime;

                if (Math.abs(delta) > 0.01) {
                    this.player.setCurrentTime(this.supposedCurrentTime).then((seconds) => {
                        // seconds = the actual time
                    })
                }
            })
        });

        const isPlayed = (time) => {
            return this.player.getPlayed().then(function (played) {
                for (var i = 0; i < played.length; i++) {
                    var start = 0, end = 0;
                    start = played[i][0];
                    end = played[i][1];
                    if (end - start < 1) {
                        continue;
                    }
                    if (time >= start && time <= end) {
                        return true;
                    }
                }
                return false;
            })
        }
    };

    /**
     * @method handleUnmute
     */
    handleUnmute = () => {
        if (this.player) {
            this.player.getMuted().then((v) => {
                this.player.setMuted(!v);

                this.setState({
                    muted: !v,
                });
            });
        }
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const { stream, streamType } = this.props;
        let { active_src, active_subtitle_src, prerecorded, clevercast_enabled, clevercast_primary_src, clevercast_backup_src } = stream;
        const { muted, subtitles, useClevercast } = this.state;

        const clevercastAvailable = !!clevercast_enabled && !!(clevercast_primary_src || clevercast_backup_src);
        const clevercastSrc = clevercast_primary_src || clevercast_backup_src;

        let vimeoSrc = active_src;

        if (streamType === 'secondary') {
            vimeoSrc = stream.secondary_stream_src;
        }

        if (subtitles) {
            vimeoSrc = active_subtitle_src;

            if (streamType === 'secondary') {
                vimeoSrc = stream.subtitle_secondary_stream_src;
            }
        }

        return (
            <>
                <div className="lg:bg-video bg-video-container mx-auto">
                    <div className="group relative aspect-w-16 aspect-h-9">
                        {useClevercast ? (
                            <iframe
                                src={clevercastSrc}
                                frameBorder="0"
                                allowFullScreen
                                className="w-full h-full inset-0 absolute rounded-xl"
                                allow="fullscreen"
                            />
                        ) : (
                            <>
                                <iframe
                                    ref={ref => this.vimeoRef = ref}
                                    src={`${vimeoSrc}${prerecorded ? `${vimeoSrc.indexOf('?') !== -1 ? '&' : '?'}autoplay=1&muted=1` : ''}`}
                                    frameBorder="0"
                                    //allow="autoplay"
                                    allowFullScreen
                                    className="w-full h-full inset-0 absolute rounded-xl"
                                    allow="fullscreen"
                                />

                                {prerecorded && (
                                    <div className={`
                                        ${!muted ? 'opacity-0' : ''}
                                        group-hover:opacity-100 absolute top-0 left-0 pt-12 w-full
                                        transition duration-200 text-center h-1/2
                                    `}>
                                        <PrimaryButton
                                            text={muted ?
                                                translation('main_stage_frontend', 'unmute') :
                                                translation('main_stage_frontend', 'mute')}
                                            onClick={this.handleUnmute}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {active_subtitle_src && !useClevercast && (
                    <div className="flex justify-center items-center mt-6">
                        <PrimaryButton
                            text={subtitles ?
                                translation('main_stage_frontend', 'disable_subtitles') :
                                translation('main_stage_frontend', 'enable_subtitles')}
                            onClick={() => this.setState({
                                subtitles: !subtitles
                            })}
                            className={"rounded-xl"}
                        />
                    </div>
                )}

                {/* Stream channel toggle moved from Tabs into the Stream tab UI */}
                <div className="my-6">
                    { this.props.streamType !== 'secondary' &&
                        <PrimaryButton
                            text="Having problems with the stream? Click here to try a different channel"
                            onClick={() => (this.props.setStreamType ? this.props.setStreamType('secondary') : null)}
                            className={"rounded-xl px-6"}
                        />
                    }

                    { this.props.streamType === 'secondary' &&
                        <PrimaryButton
                            text="Click here to return to the original stream channel"
                            onClick={() => (this.props.setStreamType ? this.props.setStreamType(null) : null)}
                            className={"rounded-xl"}
                        />
                    }
                </div>

                {useClevercast && (
                    <div className="flex justify-start items-center mt-6">
                        <SecondaryButton
                            text={translation('main_stage_frontend', 'back_to_original_stream') || 'Back to original stream'}
                            onClick={() => this.setState({ useClevercast: false })}
                            className={"rounded-xl"}
                        />
                    </div>
                )}


                {clevercastAvailable && !useClevercast && (
                    <div className="flex flex-col justify-start items-start mt-6">
                        {(User.belongsToGroup('internal-virtual') || User.belongsToGroup('external-virtual') )&&  (
                            <p className="mb-3 text-sm text-gray-700">
                                {'If you are having problems with the stream, or need to listen or read subtitles in a different language, please click the button below'}
                            </p>
                        )}
                        <PrimaryButton
                            text={"Change my language"}
                            onClick={() => this.setState({ useClevercast: true, subtitles: false })}
                            className={"rounded-xl"}
                        />
                    </div>
                )}
            </>
        );
    }
};
